import { useState, useRef, useEffect } from 'react';
import { galleryAPI } from '../../api/gallery';
import { isAllowedImageFile } from '../../utils/imageUpload';
import { getApiBaseUrl } from '../../api/apiConfig';
import './Gallery.css';

const EditGallery = ({ image, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    is_featured: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const categories = ['Portrait', 'Landscape', 'Event', 'Product', 'Nature', 'Architecture', 'Other'];

  const API_BASE = getApiBaseUrl();
  const imagePath = (image.image_url || image.image_path)?.trim();
  
  // Construct image URL (uploads served from API server root)
  let currentImageUrl = null;
  if (imagePath) {
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      currentImageUrl = imagePath;
    } else {
      const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
      currentImageUrl = `${API_BASE}${normalizedPath}`;
    }
  }

  useEffect(() => {
    // Populate form with existing data
    setFormData({
      title: image.title || image.caption || '',
      description: image.description || '',
      category: image.category || '',
      is_featured: image.is_featured || false,
    });
    if (currentImageUrl) {
      setPreview(currentImageUrl);
    }
  }, [image, currentImageUrl]);

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;

    // Validate file type
    if (!isAllowedImageFile(selectedFile)) {
      setError('Invalid file type. Please select a valid image (e.g. JPEG, PNG, WebP, AVIF).');
      return;
    }

    // Validate file size (100MB)
    if (selectedFile.size > 100 * 1024 * 1024) {
      setError('File size exceeds 100MB limit');
      return;
    }

    setFile(selectedFile);
    setError('');

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    setLoading(true);

    try {
      const updateFormData = new FormData();
      
      // Only append file if a new one was selected
      if (file) {
        updateFormData.append('image', file);
      }
      
      updateFormData.append('title', formData.title);
      updateFormData.append('description', formData.description);
      updateFormData.append('category', formData.category);
      updateFormData.append('is_featured', formData.is_featured ? 'true' : 'false');

      await galleryAPI.update(image.id, updateFormData);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update image. Please try again.');
      console.error('Update error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFile(null);
    setPreview(currentImageUrl);
    setError('');
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Gallery Image</h2>
          <button className="modal-close" onClick={handleCancel}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="upload-form">
          {/* Image Preview/Upload */}
          <div className="image-preview-section">
            <div className="current-image">
              <img src={preview} alt="Current" className="preview-image" />
              <div className="image-overlay-text">
                {file ? 'New image selected' : 'Current image'}
              </div>
            </div>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => fileInputRef.current?.click()}
            >
              {file ? 'Change Image' : 'Replace Image'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileSelect(e.target.files[0])}
              style={{ display: 'none' }}
            />
          </div>

          {/* Form Fields */}
          <div className="form-group">
            <label htmlFor="edit-title">Title *</label>
            <input
              type="text"
              id="edit-title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              placeholder="Enter image title"
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-description">Description</label>
            <textarea
              id="edit-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              placeholder="Enter image description (optional)"
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-category">Category</label>
            <select
              id="edit-category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            >
              <option value="">Select category (optional)</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={formData.is_featured}
                onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
              />
              <span>Mark as featured</span>
            </label>
          </div>

          {error && (
            <div className="error-message">{error}</div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Image'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditGallery;
